/**
 * Utility functions for word counting and validation
 */

/**
 * Count the number of words in a string
 * @param text - The text to count words from
 * @returns The number of words
 */
export function countWords(text: string): number {
  if (!text || text.trim().length === 0) {
    return 0;
  }
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
}

/**
 * Validate if text is within word limit
 * @param text - The text to validate
 * @param maxWords - Maximum allowed words (default: 100)
 * @returns Object with validation result and word count
 */
export function validateWordLimit(text: string, maxWords: number = 100): {
  isValid: boolean;
  wordCount: number;
  remaining: number;
  message?: string;
} {
  const wordCount = countWords(text);
  const remaining = maxWords - wordCount;
  const isValid = wordCount <= maxWords;

  return {
    isValid,
    wordCount,
    remaining,
    message: !isValid 
      ? `Comment must be ${maxWords} words or less. You have ${wordCount} words.`
      : undefined
  };
}

/**
 * Format word count display
 * @param current - Current word count
 * @param max - Maximum allowed words
 * @returns Formatted string for display
 */
export function formatWordCount(current: number, max: number): string {
  if (current > max) {
    return `${current}/${max} words (${current - max} over limit)`;
  }
  return `${current}/${max} words`;
}

/**
 * Get word count status color class
 * @param current - Current word count
 * @param max - Maximum allowed words
 * @returns Tailwind CSS color class
 */
export function getWordCountColorClass(current: number, max: number): string {
  const percentage = (current / max) * 100;

  if (percentage > 100) {
    return 'text-red-500'; // Over limit
  } else if (percentage > 90) {
    return 'text-yellow-500'; // Warning zone
  } else if (percentage > 75) {
    return 'text-orange-500'; // Getting close
  }
  return 'text-gray-500'; // Safe zone
}

/**
 * Check for spam patterns in text
 * @param text - The text to check
 * @returns Object with spam detection result
 */
export function detectSpam(text: string): {
  isSpam: boolean;
  reason?: string;
} {
  if (!text || text.trim().length === 0) {
    return { isSpam: false };
  }

  // Check for excessive repeated characters (e.g., "fffffffffffff")
  // 50+ same character in a row = spam
  const repeatedCharsPattern = /(.)\1{49,}/;
  if (repeatedCharsPattern.test(text)) {
    return {
      isSpam: true,
      reason: 'Excessive repeated characters detected. Please write meaningful content.'
    };
  }

  // Check for excessive repeated patterns (e.g., "hahahahaha" is ok, but 20+ repetitions is spam)
  // Pattern of 2-5 chars repeated 20+ times = spam (allows normal laughter like "hahahaha")
  const repeatedPatternMatch = text.match(/(.{2,5})\1{19,}/);
  if (repeatedPatternMatch) {
    return {
      isSpam: true,
      reason: 'Excessive repeated patterns detected. Please write meaningful content.'
    };
  }

  // Check character limit based on token holder status
  // Regular users: 1000 chars, Token holders: 2000 chars (will be passed as param)

  return { isSpam: false };
}

/**
 * Validate text with both word limit and spam detection
 * @param text - The text to validate
 * @param maxWords - Maximum allowed words
 * @param maxChars - Maximum allowed characters (default: 1000)
 * @returns Object with validation result
 */
export function validateTextContent(
  text: string,
  maxWords: number,
  maxChars: number = 1000
): {
  isValid: boolean;
  wordCount: number;
  charCount: number;
  message?: string;
} {
  const wordCount = countWords(text);
  const charCount = text.length;

  // Check spam patterns first
  const spamCheck = detectSpam(text);
  if (spamCheck.isSpam) {
    return {
      isValid: false,
      wordCount,
      charCount,
      message: spamCheck.reason
    };
  }

  // Check character limit
  if (charCount > maxChars) {
    return {
      isValid: false,
      wordCount,
      charCount,
      message: `Text is too long. Maximum ${maxChars} characters allowed. You have ${charCount} characters.`
    };
  }

  // Check word limit
  if (wordCount > maxWords) {
    return {
      isValid: false,
      wordCount,
      charCount,
      message: `Comment must be ${maxWords} words or less. You have ${wordCount} words.`
    };
  }

  return {
    isValid: true,
    wordCount,
    charCount
  };
}